/**
 * workspaceSummaryTemplate - PIPH-87 Workspace Summary Template
 *
 * Generates top-level orientation descriptions for molecular workspaces.
 * Used in both buildStructure() (ARIA tree) and buildWorkspacePackage() (LLM).
 *
 * Per-feature naming: connection descriptions resolve labels through an
 * `AtomLabelResolver` built per-molecule from the feature serialization.
 * Source-side connection atoms use the source feature's per-feature scope.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-87
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import type { ArrowIdDef } from '@alchemiesolns/arrow-def';
import { ArrowType } from '@alchemiesolns/arrow-def';
import {
  serializeFeatures,
  assembleMolecularFormula,
  getElementName,
  type AtomLabelResolver,
  type SerializationResult,
  type SerializedFeature,
  type FeatureType,
} from '@alchemiesolns/feature-extractor';
import {
  classifyRelationship,
  classifyPrimaryConnection,
  formatConnectionPhrase,
  RECOGNIZED_BRIDGING_GROUPS,
} from '@alchemiesolns/molecule-descriptions';
import { findConnectedComponents } from './graphUtils';
import { groupUnclaimedAtomFeaturesIntoClusters } from './buildStructuredAltText';
import { elementDisplayName } from './atomNaming';

// ============================================================================
// Types
// ============================================================================

/** Complexity tier for molecule descriptions */
export type ComplexityTier = 'simple' | 'moderate' | 'complex';

/** Primary structural feature types (rings, chains, etc.) */
const PRIMARY_TYPES: Set<FeatureType> = new Set([
  'ring',
  'bridged_bicyclic',
  'chain',
  'atom_cluster',
]);

/** Connection between two primary features */
interface PrimaryConnection {
  targetFeatureId: string;
  isFused: boolean;
  /** PIPH-128: Atom indices on the SOURCE feature that connect to the target */
  sourceAtomIndices: number[];
  /** PIPH-139: Bridging functional group info (if connection goes through a bridging atom) */
  bridgingGroup?: {
    feature: SerializedFeature;
    bondOrder: number | undefined;
  };
  /**
   * PIPH-212: Order of the direct inter-primary bond when there is no bridging
   * group with extra atoms (e.g. alkene/alkyne between two primaries).
   * Used to emit "by double bond" / "by triple bond" in the summary.
   */
  directBondOrder?: 1 | 2 | 3;
}

// ============================================================================
// Utility Functions
// ============================================================================

const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

function formatChargeSuffix(charge: number | undefined): string {
  if (!charge) return '';
  const sign = charge > 0 ? '⁺' : '⁻';
  const abs = Math.abs(charge);
  if (abs === 1) return sign;
  const digits = String(abs).split('').map(d => SUPERSCRIPT_DIGITS[Number(d)]).join('');
  return digits + sign;
}

// ============================================================================
// PIPH-320: Orientation-level (workspace summary) helpers.
//
// The workspace summary is a high-level "what's here / what's moving" line.
// Atoms and bonds inside arrow descriptions strip position numbers and
// feature context — the user navigates to the molecule for that detail.
// ============================================================================

/**
 * Hill-system molecular formula + charge superscript for one connected
 * component.
 *
 * The feature-extractor graph collapses hidden hydrogen atoms into each
 * parent's `implicitH` count. So a "complete" formula counts:
 *   - one entry per *graph* atom for this molecule's heavy atoms + any
 *     visible hydrogens (those that survive into the graph), and
 *   - `implicitH` extra H entries per heavy graph atom.
 * Net charge is summed across all workspace atoms in the component (the
 * graph doesn't carry charge for hidden hydrogens, but neutral hidden
 * hydrogens contribute zero anyway).
 *
 * PIPH-324: exported so buildStructure can use the same formula for molecule
 * labels in the ARIA tree, keeping names aligned with the spoken workspace
 * overview.
 */
export function moleculeFormula(
  component: { atomIds: string[] },
  workspace: MolecularWorkspace,
  featureResult: WorkspaceFeatureResult,
): string {
  const atomIdToGraphIndex = new Map<string, number>();
  for (let i = 0; i < featureResult.indexToAtomId.length; i++) {
    atomIdToGraphIndex.set(featureResult.indexToAtomId[i], i);
  }

  const symbols: string[] = [];
  let netCharge = 0;
  for (const id of component.atomIds) {
    const atom = workspace.getAtom(id);
    if (!atom) continue;
    netCharge += atom.charge ?? 0;

    // Skip workspace atoms that the graph collapsed into a parent's
    // implicit H — they'll be counted via the parent's `implicitH`.
    const gIdx = atomIdToGraphIndex.get(id);
    if (gIdx === undefined) continue;

    symbols.push(atom.symbol);
    const gAtom = featureResult.graph.atoms[gIdx];
    const implicit = gAtom?.implicitH ?? 0;
    for (let h = 0; h < implicit; h++) symbols.push('H');
  }
  return assembleMolecularFormula(symbols) + formatChargeSuffix(netCharge);
}

/** Join a list of formulas using "X", "X and Y", "X, Y, and Z" form. */
function joinAnd(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * PIPH-90 arrow-type label. Duplicated locally from `arrowDescriptionTemplate`
 * (not exported there, and small enough that re-implementing avoids threading
 * an extra export).
 */
function arrowTypeLabel(type: ArrowType): string {
  switch (type) {
    case ArrowType.RtoAtom:
    case ArrowType.RtoBond:
    case ArrowType.CBondToAtom:
    case ArrowType.CBondToBond:
      return 'Fishhook arrow';
    default:
      return 'Curved arrow';
  }
}

/**
 * Orientation-level atom name.
 *
 * Rules:
 *   - Charged carbon (±1) → "carbocation" / "carbanion" (chemistry term).
 *   - Charged carbon (±2+) → "doubly positively charged carbon" etc.
 *     (drops position, drops feature qualifier).
 *   - Charged non-carbon → "positively charged oxygen" (charge adjective +
 *     lowercased element name; no position or feature).
 *   - Atoms in a simple-molecule atom-cluster feature (≤4 atoms) → keep the
 *     "X of {formula}" form so a hydrogen in HBr still reads as
 *     "hydrogen of HBr" (the formula is the orientation-level disambiguator).
 *   - Otherwise → bare lowercased element name ("bromine", "oxygen").
 *   - Atom not found in `serialized.atoms` (explicit H folded into a
 *     parent's hydrogenCount) → fall back to the workspace symbol map.
 */
function simpleAtomName(
  atomIndex: number | undefined,
  serialized: SerializationResult,
  workspace: MolecularWorkspace,
  atomIdToIndex: Map<string, number>,
): string {
  if (atomIndex === undefined) return 'atom';

  const atom = serialized.atoms.find(a => a.index === atomIndex);
  if (!atom) {
    // Atom-N leak fix: lookup the workspace symbol via the inverse map.
    let symbol: string | undefined;
    for (const [id, idx] of atomIdToIndex.entries()) {
      if (idx === atomIndex) {
        symbol = workspace.getAtom(id)?.symbol;
        break;
      }
    }
    if (!symbol) return 'atom';
    return (getElementName(symbol) ?? symbol).toLowerCase();
  }

  // Charged-carbon → chemistry term.
  if (atom.symbol === 'C' && atom.charge !== 0) {
    const mag = Math.abs(atom.charge);
    if (mag === 1) return atom.charge > 0 ? 'carbocation' : 'carbanion';
    const adj = atom.charge > 0 ? 'positively' : 'negatively';
    const multiplier = mag === 2 ? 'doubly ' : `${mag}-times `;
    return `${multiplier}${adj} charged carbon`;
  }

  // Simple-molecule atom-cluster feature → "X of {formula}" form (mirrors
  // the PIPH-122 simple-molecule path in `getAtomNameWithContext`).
  const simpleFeature = serialized.features.find(
    f =>
      atom.featureIds.includes(f.id) &&
      f.type === 'atom' &&
      f.atomIndices.length <= 4,
  );
  if (simpleFeature && simpleFeature.atomIndices.length > 1) {
    const elementName = (getElementName(atom.symbol) ?? atom.symbol).toLowerCase();
    return `${elementName} of ${simpleFeature.name}`;
  }

  // Functional-group context: when the atom belongs to a single-atom-anchor
  // FG (alcohol, amine, ether, sulfide, thiol, etc.), surface that as
  // disambiguating context — "oxygen of alcohol" rather than bare "oxygen".
  // Don't apply for multi-atom FGs (ester, amide, etc.) where the atom is
  // one of many and the prose would be ambiguous.
  const fgFeature = serialized.features.find(
    f =>
      atom.featureIds.includes(f.id) &&
      f.type === 'functional_group' &&
      isSingleAtomFunctionalGroup(f),
  );
  if (fgFeature) {
    const elementName = elementDisplayName(atom.symbol, {
      charge: atom.charge,
      radicals: atom.radicals,
    });
    return `${elementName} of ${fgFeature.name}`;
  }

  // Charged non-carbon / radical → use the shared prose helper.
  return elementDisplayName(atom.symbol, {
    charge: atom.charge,
    radicals: atom.radicals,
  });
}

const SINGLE_ATOM_FG_BASES = new Set(['alcohol', 'amine', 'ether', 'sulfide', 'thiol']);

function isSingleAtomFunctionalGroup(f: SerializedFeature): boolean {
  if (!f.internalName) return false;
  const baseType = f.internalName.split('.')[0];
  return SINGLE_ATOM_FG_BASES.has(baseType);
}

/**
 * Orientation-level bond phrase. Returns e.g. "carbon-bromine bond" or
 * "carbon-carbon double bond".
 *
 * PIPH-320 ordering: carbon first when present, then hydrogen last,
 * otherwise alphabetical. The two card-spec examples that pin this:
 *   "carbon-bromine bond" (C present → first)
 *   "bromine-hydrogen bond" (no C → alphabetical, H last)
 */
function simpleBondPhrase(
  bondId: string,
  workspace: MolecularWorkspace,
): string {
  const bond = workspace.getBond(bondId);
  if (!bond) return 'bond';

  const s1 = workspace.getAtom(bond.a1.id)?.symbol ?? '';
  const s2 = workspace.getAtom(bond.a2.id)?.symbol ?? '';
  const [leadSym, tailSym] = orderBondSymbols(s1, s2);

  const e1 = (getElementName(leadSym) ?? leadSym).toLowerCase();
  const e2 = (getElementName(tailSym) ?? tailSym).toLowerCase();

  const orderWord =
    bond.order === 2 ? ' double' : bond.order === 3 ? ' triple' : '';
  return `${e1}-${e2}${orderWord} bond`;
}

/**
 * Bond-symbol ordering for orientation-level prose: carbon first when
 * present, hydrogen last when present, otherwise alphabetical.
 */
function orderBondSymbols(s1: string, s2: string): [string, string] {
  const rank = (s: string): number => {
    if (s === 'C') return 0;
    if (s === 'H') return 2;
    return 1;
  };
  const r1 = rank(s1);
  const r2 = rank(s2);
  if (r1 !== r2) return r1 < r2 ? [s1, s2] : [s2, s1];
  // Same rank — alphabetical for stability.
  return s1 <= s2 ? [s1, s2] : [s2, s1];
}

/**
 * PIPH-320: Orientation-level arrow description for the workspace summary.
 * Same shape as `describeArrow` but strips position numbers and feature
 * context; substitutes chemistry terms ("carbocation", "carbon-oxygen
 * double bond") where the spec defines them.
 *
 * `mentioned` tracks atom indices already named in the workspace summary
 * so far. Mirrors PIPH-319's brevity rule: when an atom appears in a
 * later arrow that's already been named, its feature suffix
 * (e.g. "of HBr") is dropped on repeat.
 */
function describeArrowOrientation(
  arrow: ArrowIdDef,
  workspace: MolecularWorkspace,
  serialized: SerializationResult,
  atomIdToIndex: Map<string, number>,
  mentioned: Set<number>,
): string {
  const label = arrowTypeLabel(arrow.type);

  const renderAtom = (idx: number | undefined): string => {
    const name = simpleAtomName(idx, serialized, workspace, atomIdToIndex);
    if (idx !== undefined && mentioned.has(idx)) {
      // Repeat mention: drop the " of {feature}" simple-molecule qualifier.
      const ofIdx = name.lastIndexOf(' of ');
      if (ofIdx > 0) return name.slice(0, ofIdx);
    }
    if (idx !== undefined) mentioned.add(idx);
    return name;
  };

  // Source.
  let source: string;
  switch (arrow.type) {
    case ArrowType.LPtoAtom:
    case ArrowType.LPtoBond:
      source = `lone pair on ${renderAtom(atomIdToIndex.get(arrow.srcId))}`;
      break;
    case ArrowType.RtoAtom:
    case ArrowType.RtoBond:
      source = `radical on ${renderAtom(atomIdToIndex.get(arrow.srcId))}`;
      break;
    default: {
      // Bond source — register each endpoint as mentioned for brevity.
      const srcBond = workspace.getBond(arrow.srcId);
      if (srcBond) {
        const a1Idx = atomIdToIndex.get(srcBond.a1.id);
        const a2Idx = atomIdToIndex.get(srcBond.a2.id);
        if (a1Idx !== undefined) mentioned.add(a1Idx);
        if (a2Idx !== undefined) mentioned.add(a2Idx);
      }
      source = simpleBondPhrase(arrow.srcId, workspace);
    }
  }

  // Destination.
  let destination: string;
  switch (arrow.type) {
    case ArrowType.LPtoAtom:
    case ArrowType.BondToAtom:
    case ArrowType.RtoAtom:
    case ArrowType.CBondToAtom:
      destination = renderAtom(atomIdToIndex.get(arrow.dstId));
      break;
    case ArrowType.LPtoBond:
    case ArrowType.BondToBond:
    case ArrowType.RtoBond:
    case ArrowType.CBondToBond: {
      const dstBond = workspace.getBond(arrow.dstId);
      if (dstBond) {
        const a1Idx = atomIdToIndex.get(dstBond.a1.id);
        const a2Idx = atomIdToIndex.get(dstBond.a2.id);
        if (a1Idx !== undefined) mentioned.add(a1Idx);
        if (a2Idx !== undefined) mentioned.add(a2Idx);
      }
      destination = simpleBondPhrase(arrow.dstId, workspace);
      break;
    }
    default:
      destination = 'destination';
  }

  return `${label} from ${source} to ${destination}`;
}

/**
 * Convert a number to its word form (for counts up to 10)
 */
function numberToWord(n: number): string {
  const words: Record<number, string> = {
    1: 'One',
    2: 'Two',
    3: 'Three',
    4: 'Four',
    5: 'Five',
    6: 'Six',
    7: 'Seven',
    8: 'Eight',
    9: 'Nine',
    10: 'Ten',
  };
  return words[n] || String(n);
}

/**
 * Join a list with commas and "and" (Oxford comma style)
 */
function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * PIPH-128: Get the position label for a connection point on a feature.
 * Returns lowercase label like "carbon-1" for use in "connected at carbon-1 to".
 * Returns empty string if no connection atoms supplied. Returns "atom-?" loud
 * failure marker if the resolver returns nothing — never synthesizes from local
 * positions.
 *
 * The source atom is in `sourceFeature`, so we resolve in that feature's
 * per-feature scope: the position label matches the source feature's own
 * numbering of its atoms.
 */
function getConnectionPositionLabel(
  sourceFeature: SerializedFeature,
  sourceAtomIndices: number[],
  resolve: AtomLabelResolver,
): string {
  if (sourceAtomIndices.length === 0) return '';
  const atomIdx = sourceAtomIndices[0];
  const label = resolve(atomIdx, sourceFeature.id);
  if (!label) return 'atom-?';
  return label.toLowerCase();
}

/**
 * Strip letter suffix from feature names (e.g., "benzene ring-A" → "benzene ring")
 */
function stripLetterSuffix(name: string): string {
  return name.replace(/-[A-Z]+$/, '');
}

// ============================================================================
// PIPH-139: Bridging Group Helpers
// ============================================================================

/**
 * PIPH-139 / PIPH-212: RECOGNIZED_BRIDGING_GROUPS now lives in
 * `@alchemiesolns/molecule-descriptions`.
 */
function isRecognizedBridgingGroup(feature: SerializedFeature): boolean {
  return RECOGNIZED_BRIDGING_GROUPS.has(feature.internalName);
}

/**
 * PIPH-139: Format bond order label including single bonds.
 */
function formatBondOrderLabelFull(order: number | undefined): string {
  if (order === 3) return 'triple bond';
  if (order === 2) return 'double bond';
  return 'single bond';
}

/**
 * PIPH-139: Format the bridging phrase for a connection.
 * - Recognized groups: "through {group name}"
 * - Unrecognized groups: "through {bond order} to {formula}"
 *
 * PIPH-310: When the unrecognized bridging group is a singleton atom-type
 * feature, the target name swaps to prose form ("positively charged
 * nitrogen") via `elementDisplayName` instead of the feature's title-case
 * name ("Nitrogen").
 */
function formatBridgingPhrase(
  bridgingGroup: { feature: SerializedFeature; bondOrder: number | undefined },
  serialized: SerializationResult,
): string {
  if (isRecognizedBridgingGroup(bridgingGroup.feature)) {
    // Use the display name for recognized groups
    return `through ${bridgingGroup.feature.name}`;
  } else {
    // Use bond order + formula for unrecognized groups
    const bondLabel = formatBondOrderLabelFull(bridgingGroup.bondOrder);
    const targetName =
      bridgingGroup.feature.type === 'atom' &&
      bridgingGroup.feature.atomIndices.length === 1
        ? (() => {
            const a = serialized.atoms.find(
              x => x.index === bridgingGroup.feature.atomIndices[0],
            );
            return a
              ? elementDisplayName(a.symbol, {
                  charge: a.charge,
                  radicals: a.radicals,
                })
              : bridgingGroup.feature.name;
          })()
        : bridgingGroup.feature.name;
    return `through ${bondLabel} to ${targetName}`;
  }
}

/**
 * PIPH-212: Build the connector portion of "connected{phrase} to {target}".
 * Returns "" for plain single-bond direct connections (existing behavior),
 * "by double bond"/"by triple bond" for alkene/alkyne direct connections,
 * or the existing bridging phrase for true bridges.
 */
function formatConnectorPhrase(
  conn: PrimaryConnection,
  serialized: SerializationResult,
): string {
  if (conn.bridgingGroup) return ` ${formatBridgingPhrase(conn.bridgingGroup, serialized)}`;
  if (conn.directBondOrder === 2 || conn.directBondOrder === 3) {
    return ` ${formatConnectionPhrase({
      kind: 'direct',
      group: { name: '' } as SerializedFeature,
      bondOrder: conn.directBondOrder,
    })}`;
  }
  return '';
}

/**
 * Pluralize a feature or group name
 */
function pluralize(name: string): string {
  // Handle compound names ending in common suffixes
  if (name.endsWith(' group')) return name + 's';
  if (name.endsWith(' ring')) return name + 's';
  if (name.endsWith(' chain')) return name + 's';
  if (name.endsWith(' system')) return name + 's';

  // Standard English pluralization
  if (name.endsWith('s') || name.endsWith('x') || name.endsWith('ch') || name.endsWith('sh')) {
    return name + 'es';
  }
  if (name.endsWith('y') && !/[aeiou]y$/.test(name)) {
    return name.slice(0, -1) + 'ies';
  }

  return name + 's';
}

/**
 * Check if a feature type is a primary structural feature
 */
function isPrimaryType(type: FeatureType): boolean {
  return PRIMARY_TYPES.has(type);
}

// ============================================================================
// Core Selection and Connection Analysis
// ============================================================================

/**
 * Compute bidirectional connections between primary features.
 * Returns a map from feature ID to array of connections.
 */
function computePrimaryConnections(
  primaries: SerializedFeature[],
  serialized: SerializationResult
): Map<string, PrimaryConnection[]> {
  const connectionMap = new Map<string, PrimaryConnection[]>();
  const primaryIds = new Set(primaries.map(f => f.id));

  // Initialize empty connection arrays
  for (const primary of primaries) {
    connectionMap.set(primary.id, []);
  }

  // Build lookup: atom index -> primary feature ID
  const atomToPrimary = new Map<number, string>();
  for (const primary of primaries) {
    for (const atomIdx of primary.atomIndices) {
      atomToPrimary.set(atomIdx, primary.id);
    }
  }

  // Track pairs we've already processed
  const processedPairs = new Set<string>();

  // PIPH-128: Build lookup from atom index to connecting atoms via bonds
  const atomConnections = new Map<number, number[]>();
  for (const bond of serialized.bonds) {
    const [a, b] = bond.atomIndices;
    if (!atomConnections.has(a)) atomConnections.set(a, []);
    if (!atomConnections.has(b)) atomConnections.set(b, []);
    atomConnections.get(a)!.push(b);
    atomConnections.get(b)!.push(a);
  }

  // Method 1: Direct connections via connectionAtomIndices
  for (const source of primaries) {
    for (const connAtomIdx of source.connectionAtomIndices) {
      const targetId = atomToPrimary.get(connAtomIdx);
      if (targetId && targetId !== source.id && primaryIds.has(targetId)) {
        const pairKey = [source.id, targetId].sort().join('|');
        if (!processedPairs.has(pairKey)) {
          processedPairs.add(pairKey);

          // PIPH-128: Find source atom indices - atoms in source that bond to atoms in target
          // connAtomIdx is on the target; find which source atoms connect to it
          const sourceAtomSet = new Set(source.atomIndices);
          const connectedToTarget = atomConnections.get(connAtomIdx) || [];
          const sourceAtomIndices = connectedToTarget.filter(idx => sourceAtomSet.has(idx));

          // Find target atom indices - atoms in target that bond to atoms in source
          const target = primaries.find(p => p.id === targetId);
          const targetAtomSet = target ? new Set(target.atomIndices) : new Set<number>();
          const targetAtomIndices: number[] = [];
          // PIPH-212: Capture the order of the direct inter-primary bond so
          // alkene/alkyne connections can be described as "by double bond".
          let directBondOrder: 1 | 2 | 3 | undefined;
          for (const srcIdx of sourceAtomIndices) {
            const connectedToSource = atomConnections.get(srcIdx) || [];
            for (const tgtIdx of connectedToSource) {
              if (targetAtomSet.has(tgtIdx) && !targetAtomIndices.includes(tgtIdx)) {
                targetAtomIndices.push(tgtIdx);
                if (directBondOrder === undefined) {
                  const bond = serialized.bonds.find(b =>
                    (b.atomIndices[0] === srcIdx && b.atomIndices[1] === tgtIdx) ||
                    (b.atomIndices[0] === tgtIdx && b.atomIndices[1] === srcIdx)
                  );
                  const ord = bond?.order;
                  if (ord === 2 || ord === 3) directBondOrder = ord;
                }
              }
            }
          }

          // Add connection from source to target
          connectionMap.get(source.id)!.push({
            targetFeatureId: targetId,
            isFused: false,
            sourceAtomIndices,
            directBondOrder,
          });

          // Add reciprocal connection
          connectionMap.get(targetId)!.push({
            targetFeatureId: source.id,
            isFused: false,
            sourceAtomIndices: targetAtomIndices,
            directBondOrder,
          });
        }
      }
    }
  }

  // Method 2: Fused relationships
  for (const source of primaries) {
    if (source.fusedTo) {
      for (const fusedId of source.fusedTo) {
        if (primaryIds.has(fusedId)) {
          const pairKey = [source.id, fusedId].sort().join('|');
          if (!processedPairs.has(pairKey)) {
            processedPairs.add(pairKey);

            // Add fused connection from source to target
            // Fused connections don't use position labels, so empty array
            connectionMap.get(source.id)!.push({
              targetFeatureId: fusedId,
              isFused: true,
              sourceAtomIndices: [],
            });

            // Add reciprocal fused connection
            connectionMap.get(fusedId)!.push({
              targetFeatureId: source.id,
              isFused: true,
              sourceAtomIndices: [],
            });
          } else {
            // Update existing connection to mark as fused
            const sourceConns = connectionMap.get(source.id)!;
            const existingConn = sourceConns.find(c => c.targetFeatureId === fusedId);
            if (existingConn) existingConn.isFused = true;

            const targetConns = connectionMap.get(fusedId)!;
            const reciprocalConn = targetConns.find(c => c.targetFeatureId === source.id);
            if (reciprocalConn) reciprocalConn.isFused = true;
          }
        }
      }
    }
  }

  // Method 3: Bridging functional groups
  for (const fg of serialized.features) {
    if (fg.type !== 'functional_group') continue;
    if (!fg.parentFeatureId || !primaryIds.has(fg.parentFeatureId)) continue;

    for (const connAtomIdx of fg.connectionAtomIndices) {
      const touchedId = atomToPrimary.get(connAtomIdx);
      if (touchedId && touchedId !== fg.parentFeatureId && primaryIds.has(touchedId)) {
        const pairKey = [fg.parentFeatureId, touchedId].sort().join('|');
        if (!processedPairs.has(pairKey)) {
          processedPairs.add(pairKey);

          // PIPH-128: For bridging groups, find which atoms on parent connect to the bridge
          // and which atoms on touched connect from the bridge
          const parent = primaries.find(p => p.id === fg.parentFeatureId);
          const touched = primaries.find(p => p.id === touchedId);

          // Find parent atoms that connect to the bridging group
          const parentAtomSet = parent ? new Set(parent.atomIndices) : new Set<number>();
          const parentAtomIndices: number[] = [];
          let parentBondOrder: number | undefined;
          for (const fgAtom of fg.atomIndices) {
            const connectedTo = atomConnections.get(fgAtom) || [];
            for (const idx of connectedTo) {
              if (parentAtomSet.has(idx) && !parentAtomIndices.includes(idx)) {
                parentAtomIndices.push(idx);
                // PIPH-139: Capture bond order from parent to bridging group
                if (parentBondOrder === undefined) {
                  const bond = serialized.bonds.find(b =>
                    (b.atomIndices[0] === fgAtom && b.atomIndices[1] === idx) ||
                    (b.atomIndices[0] === idx && b.atomIndices[1] === fgAtom)
                  );
                  parentBondOrder = bond?.order;
                }
              }
            }
          }

          // Find touched atoms that connect from the bridging group
          const touchedAtomSet = touched ? new Set(touched.atomIndices) : new Set<number>();
          const touchedAtomIndices: number[] = [];
          let touchedBondOrder: number | undefined;
          for (const fgAtom of fg.atomIndices) {
            const connectedTo = atomConnections.get(fgAtom) || [];
            for (const idx of connectedTo) {
              if (touchedAtomSet.has(idx) && !touchedAtomIndices.includes(idx)) {
                touchedAtomIndices.push(idx);
                // PIPH-139: Capture bond order from bridging group to touched
                if (touchedBondOrder === undefined) {
                  const bond = serialized.bonds.find(b =>
                    (b.atomIndices[0] === fgAtom && b.atomIndices[1] === idx) ||
                    (b.atomIndices[0] === idx && b.atomIndices[1] === fgAtom)
                  );
                  touchedBondOrder = bond?.order;
                }
              }
            }
          }

          // PIPH-212: Distinguish a true bridge (ester, ether, …) from an
          // alkene/alkyne where the fg's atoms already live inside the two
          // primaries — that case is a direct bond labeled by bond order.
          const parentFeature = parent;
          const touchedFeature = touched;
          const info = parentFeature && touchedFeature
            ? classifyPrimaryConnection(fg, parentFeature, touchedFeature, serialized)
            : { kind: 'bridge' as const };

          if (info.kind === 'direct') {
            // Source/target should be the fg atoms living in each primary so
            // posLabel resolves on the parent side. Bond order is the order
            // of the direct inter-primary bond (the C=C itself).
            const parentAtomSetForFg = parentAtomSet;
            const touchedAtomSetForFg = touchedAtomSet;
            const fgInParent = fg.atomIndices.filter(idx => parentAtomSetForFg.has(idx));
            const fgInTouched = fg.atomIndices.filter(idx => touchedAtomSetForFg.has(idx));
            const order = info.bondOrder;
            const directOrder: 1 | 2 | 3 | undefined =
              order === 2 || order === 3 ? order : undefined;

            connectionMap.get(fg.parentFeatureId)!.push({
              targetFeatureId: touchedId,
              isFused: false,
              sourceAtomIndices: fgInParent,
              directBondOrder: directOrder,
            });
            connectionMap.get(touchedId)!.push({
              targetFeatureId: fg.parentFeatureId,
              isFused: false,
              sourceAtomIndices: fgInTouched,
              directBondOrder: directOrder,
            });
          } else {
            // PIPH-139: True bridging group — record it as before.
            connectionMap.get(fg.parentFeatureId)!.push({
              targetFeatureId: touchedId,
              isFused: false,
              sourceAtomIndices: parentAtomIndices,
              bridgingGroup: { feature: fg, bondOrder: parentBondOrder },
            });

            connectionMap.get(touchedId)!.push({
              targetFeatureId: fg.parentFeatureId,
              isFused: false,
              sourceAtomIndices: touchedAtomIndices,
              bridgingGroup: { feature: fg, bondOrder: touchedBondOrder },
            });
          }
        }
      }
    }
  }

  // Method 4: Bridging atoms (PIPH-139)
  // Atoms that touch two different primary features act as bridges
  for (const atomFeature of serialized.features) {
    if (atomFeature.type !== 'atom') continue;

    // Find all primaries this atom touches
    const touchedPrimaryIds = new Set<string>();
    for (const connAtomIdx of atomFeature.connectionAtomIndices) {
      const primaryId = atomToPrimary.get(connAtomIdx);
      if (primaryId) touchedPrimaryIds.add(primaryId);
    }

    // If touches exactly 2 primaries, it's a bridge
    if (touchedPrimaryIds.size !== 2) continue;

    const [primaryA, primaryB] = Array.from(touchedPrimaryIds);
    const pairKey = [primaryA, primaryB].sort().join('|');
    if (processedPairs.has(pairKey)) continue;
    processedPairs.add(pairKey);

    // Find atom indices on each primary that connect to this bridging atom
    const primaryAFeature = primaries.find(p => p.id === primaryA);
    const primaryBFeature = primaries.find(p => p.id === primaryB);
    const primaryAAtomSet = primaryAFeature ? new Set(primaryAFeature.atomIndices) : new Set<number>();
    const primaryBAtomSet = primaryBFeature ? new Set(primaryBFeature.atomIndices) : new Set<number>();

    const sourceAtomIndicesA: number[] = [];
    const sourceAtomIndicesB: number[] = [];
    let bondOrderA: number | undefined;
    let bondOrderB: number | undefined;

    for (const atomIdx of atomFeature.atomIndices) {
      const connectedTo = atomConnections.get(atomIdx) || [];
      for (const idx of connectedTo) {
        if (primaryAAtomSet.has(idx) && !sourceAtomIndicesA.includes(idx)) {
          sourceAtomIndicesA.push(idx);
          if (bondOrderA === undefined) {
            const bond = serialized.bonds.find(b =>
              (b.atomIndices[0] === atomIdx && b.atomIndices[1] === idx) ||
              (b.atomIndices[0] === idx && b.atomIndices[1] === atomIdx)
            );
            bondOrderA = bond?.order;
          }
        }
        if (primaryBAtomSet.has(idx) && !sourceAtomIndicesB.includes(idx)) {
          sourceAtomIndicesB.push(idx);
          if (bondOrderB === undefined) {
            const bond = serialized.bonds.find(b =>
              (b.atomIndices[0] === atomIdx && b.atomIndices[1] === idx) ||
              (b.atomIndices[0] === idx && b.atomIndices[1] === atomIdx)
            );
            bondOrderB = bond?.order;
          }
        }
      }
    }

    // Add bidirectional connections with bridging atom info
    connectionMap.get(primaryA)!.push({
      targetFeatureId: primaryB,
      isFused: false,
      sourceAtomIndices: sourceAtomIndicesA,
      bridgingGroup: { feature: atomFeature, bondOrder: bondOrderA },
    });

    connectionMap.get(primaryB)!.push({
      targetFeatureId: primaryA,
      isFused: false,
      sourceAtomIndices: sourceAtomIndicesB,
      bridgingGroup: { feature: atomFeature, bondOrder: bondOrderB },
    });
  }

  return connectionMap;
}

/**
 * Determine the core feature: the primary feature with the most connections
 * to other primaries, using atom count as tiebreaker.
 */
function determineCore(
  primaries: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>
): SerializedFeature | undefined {
  if (primaries.length === 0) return undefined;

  // Build index map for deterministic fallback
  const indexMap = new Map(primaries.map((f, i) => [f.id, i]));

  const sorted = [...primaries].sort((a, b) => {
    // Sort by connection count (desc)
    const connDiff =
      (connectionMap.get(b.id)?.length || 0) - (connectionMap.get(a.id)?.length || 0);
    if (connDiff !== 0) return connDiff;

    // Then by atom count (desc)
    const atomDiff = b.atomIndices.length - a.atomIndices.length;
    if (atomDiff !== 0) return atomDiff;

    // Deterministic fallback: first in feature order
    return indexMap.get(a.id)! - indexMap.get(b.id)!;
  });

  return sorted[0];
}

// ============================================================================
// Complexity Classification
// ============================================================================

/**
 * Classify a molecule's complexity tier based on:
 * - Number of primary features
 * - Number of functional groups
 * - Number of connections from core to other primaries
 */
function classifyComplexity(
  primaries: SerializedFeature[],
  functionalGroups: SerializedFeature[],
  coreConnectionCount: number
): ComplexityTier {
  // Complex takes precedence
  if (primaries.length >= 4 || functionalGroups.length >= 6) {
    return 'complex';
  }

  // Moderate: core connects to 2-3 other primaries
  if (coreConnectionCount >= 2) {
    return 'moderate';
  }

  // Simple: core connects to 0-1 other primaries
  return 'simple';
}

// ============================================================================
// Duplicate Grouping
// ============================================================================

/**
 * Group duplicate primary features by base name and format with count words.
 * E.g., ["benzene ring", "benzene ring", "3-carbon chain"] →
 *       ["two benzene rings", "a 3-carbon chain"]
 */
function groupDuplicatePrimaries(primaries: SerializedFeature[]): string[] {
  const counts = new Map<string, number>();

  for (const primary of primaries) {
    const baseName = stripLetterSuffix(primary.name);
    counts.set(baseName, (counts.get(baseName) || 0) + 1);
  }

  const results: string[] = [];
  for (const [name, count] of counts) {
    if (count === 1) {
      results.push(`a ${name}`);
    } else {
      results.push(`${numberToWord(count).toLowerCase()} ${pluralize(name)}`);
    }
  }

  // Sort alphabetically for complex tier
  return results.sort();
}

/**
 * Group duplicate functional groups and format based on tier.
 * - Simple/Moderate: count words ("two methyl groups")
 * - Complex: pluralize without count ("esters", "alcohols")
 */
function groupDuplicateFunctionalGroups(
  groups: SerializedFeature[],
  tier: ComplexityTier
): string[] {
  const counts = new Map<string, number>();

  for (const group of groups) {
    // PIPH-151: Strip "of {parent}" suffix - parent qualifiers belong in
    // feature descriptions, not workspace/molecule summaries
    const baseName = stripLetterSuffix(stripOfSuffix(group.name));
    counts.set(baseName, (counts.get(baseName) || 0) + 1);
  }

  const results: string[] = [];
  for (const [name, count] of counts) {
    if (tier === 'complex') {
      // Complex: pluralize without count
      results.push(count === 1 ? name : pluralize(name));
    } else {
      // Simple/Moderate: count word
      if (count === 1) {
        results.push(name);
      } else {
        results.push(`${numberToWord(count).toLowerCase()} ${pluralize(name)}`);
      }
    }
  }

  // Sort alphabetically for complex tier
  if (tier === 'complex') {
    return results.sort();
  }

  return results;
}

// ============================================================================
// Tier-Specific Summary Builders
// ============================================================================

/**
 * Build a simple tier summary.
 * Pattern: "A {core with groups}[, and a {connected primary with groups}]."
 *
 * PIPH-106: Pattern D groups (integrated into ring) are folded into the base
 * sentence using element names (for single-atom groups) or group names
 * (for multi-atom groups like ester/lactone).
 *
 * Per-feature labels: source-side connection atoms resolve in the source
 * feature's per-feature scope.
 */
function buildSimpleSummary(
  core: SerializedFeature,
  primaries: SerializedFeature[],
  functionalGroups: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  const parts: string[] = [];

  // PIPH-212: Functional groups that span two primaries are described in the
  // connection sentence ("connected at carbon-X by double bond to ..."), so
  // they should NOT also appear as substituents ("with alkene").
  const isInterPrimaryGroup = (fg: SerializedFeature): boolean =>
    classifyRelationship(fg, serialized).kind === 'between';

  // Get functional groups on core and separate Pattern D from others
  const coreGroups = functionalGroups.filter(fg => fg.parentFeatureId === core.id);
  const patternDGroups = filterPatternDGroups(coreGroups.filter(fg => isPatternDGroup(fg, serialized)));
  const otherGroups = coreGroups.filter(
    fg => !isPatternDGroup(fg, serialized) && !isInterPrimaryGroup(fg),
  );

  // Build core part - fold Pattern D groups into base sentence
  let corePart = `A ${core.name}`;
  const withParts: string[] = [];

  // PIPH-106: Pattern D groups use element name (nitrogen) or group name (ester)
  if (patternDGroups.length > 0) {
    const patternDNames = patternDGroups.map(fg => getPatternDName(fg, serialized));
    withParts.push(...patternDNames);
  }

  // Non-Pattern-D groups are listed normally
  if (otherGroups.length > 0) {
    const groupedOtherGroups = groupDuplicateFunctionalGroups(otherGroups, 'simple');
    withParts.push(...groupedOtherGroups);
  }

  // PIPH-131: Include attached atom-type features (like H₂O⁺)
  // PIPH-234: cluster-aware — multi-atom unrecognized clusters roll into
  // one display name (e.g. "HO₂") rather than producing two separate
  // "Oxygen-A" / "Oxygen-B" entries.
  const coreAtomClusters = getAttachedAtomClusters(core.id, serialized);
  if (coreAtomClusters.length > 0) {
    withParts.push(...coreAtomClusters.map(c => c.displayName));
  }

  if (withParts.length > 0) {
    corePart += ` with ${joinList(withParts)}`;
  }
  parts.push(corePart);

  // Add connected primaries
  const coreConnections = connectionMap.get(core.id) || [];
  for (const conn of coreConnections) {
    const connected = primaries.find(p => p.id === conn.targetFeatureId);
    if (!connected) continue;

    // Get functional groups on connected primary and separate Pattern D
    const connectedGroups = functionalGroups.filter(fg => fg.parentFeatureId === connected.id);
    const connPatternD = filterPatternDGroups(connectedGroups.filter(fg => isPatternDGroup(fg, serialized)));
    const connOther = connectedGroups.filter(
      fg => !isPatternDGroup(fg, serialized) && !isInterPrimaryGroup(fg),
    );

    let connectedPart = connected.name;
    const connWithParts: string[] = [];

    if (connPatternD.length > 0) {
      connWithParts.push(...connPatternD.map(fg => getPatternDName(fg, serialized)));
    }
    if (connOther.length > 0) {
      connWithParts.push(...groupDuplicateFunctionalGroups(connOther, 'simple'));
    }

    // PIPH-131 / PIPH-234: include cluster-rolled-up unrecognized atoms
    const connAtomClusters = getAttachedAtomClusters(connected.id, serialized);
    if (connAtomClusters.length > 0) {
      connWithParts.push(...connAtomClusters.map(c => c.displayName));
    }

    if (connWithParts.length > 0) {
      connectedPart += ` with ${joinList(connWithParts)}`;
    }

    if (conn.isFused) {
      parts.push(`fused to a ${connectedPart}`);
    } else {
      // PIPH-128: Use "connected at position to" for non-fused connections
      // PIPH-139: Include bridging group info when present
      // PIPH-212: Include "by double bond" for alkene/alkyne direct connections.
      // Source atom is in `core`'s scope so the resolver receives core.id.
      const posLabel = getConnectionPositionLabel(core, conn.sourceAtomIndices, resolve);
      const connectorPhrase = formatConnectorPhrase(conn, serialized);
      if (posLabel) {
        parts.push(`connected at ${posLabel}${connectorPhrase} to ${connectedPart}`);
      } else {
        parts.push(`connected${connectorPhrase} to ${connectedPart}`);
      }
    }
  }

  return parts.join(', ') + '.';
}

/**
 * PIPH-106: Groups that should be treated as Pattern D when most atoms are
 * inside a ring, even if the carbonyl oxygen (=O) is geometrically outside.
 * These are cyclic carbonyl groups (lactones, lactams) where the C-O-C or
 * C-N-C portion is inside the ring but the C=O is outside.
 */
const SPECIAL_INTEGRATED_GROUPS = new Set([
  'carbonyl.ester',    // Lactones - ester integrated in ring
  'carbonyl.amide',    // Lactams - amide integrated in ring
  'carbonyl.amide.1',
  'carbonyl.amide.2',
  'carbonyl.amide.3',
]);

/**
 * PIPH-106: Check if a functional group is Pattern D (integrated INTO a ring).
 * Pattern D groups should be folded into the base sentence, not listed separately.
 * Uses classifyRelationship() from molecule-descriptions package (PIPH-60).
 *
 * Pattern D groups include: secondary amine, tertiary amine, ether, sulfide,
 * ester/lactone, amide/lactam when their relationship kind is "in".
 *
 * Enhanced to handle lactones/lactams where the carbonyl oxygen is
 * geometrically outside the ring but the group is chemically integrated.
 */
function isPatternDGroup(
  fg: SerializedFeature,
  result: SerializationResult
): boolean {
  const rel = classifyRelationship(fg, result);

  // Standard Pattern D: classifyRelationship returns "in"
  if (rel.kind === 'in') {
    return true;
  }

  // PIPH-106: Special handling for lactones/lactams
  // These groups have carbonyl oxygen outside the ring but should still be Pattern D
  if (fg.internalName && SPECIAL_INTEGRATED_GROUPS.has(fg.internalName)) {
    // Check if this is actually a cyclic version (lactone/lactam)
    // by verifying the parent is a ring and most atoms are inside
    const parent = result.features.find(f => f.id === fg.parentFeatureId);
    if (parent && parent.type === 'ring') {
      const parentAtomSet = new Set(parent.atomIndices);
      const insideCount = fg.atomIndices.filter(idx => parentAtomSet.has(idx)).length;
      const totalCount = fg.atomIndices.length;

      // If at least half the atoms are inside the ring, treat as Pattern D
      // For ester: C and O (ester) inside, O (carbonyl) outside -> 2/3 inside
      if (insideCount >= totalCount / 2) {
        return true;
      }
    }
  }

  return false;
}

/**
 * PIPH-106: Helper to strip "of {parent}" suffix from a group name.
 */
function stripOfSuffix(name: string): string {
  const ofIndex = name.indexOf(' of ');
  return ofIndex > 0 ? name.substring(0, ofIndex) : name;
}

/**
 * PIPH-106: Get the appropriate name for a Pattern D group in the base sentence.
 * - Single-atom groups (secondary amine, ether, sulfide): use element name
 * - Multi-atom groups (ester, amide, lactone, lactam): use functional group name
 *
 * Internal names use format "type.subtype" (e.g., "amine.2" for secondary amine,
 * "carbonyl.ester" for ester). We check both the base type and specific patterns.
 *
 * Note: workspace summary does NOT include position info (unlike fact block).
 */
function getPatternDName(
  fg: SerializedFeature,
  serialized: SerializationResult,
): string {
  // PIPH-310: Map base types to element symbols; the new prose helper
  // (`elementDisplayName`) does the symbol→lowercase-name conversion and
  // applies charge/radical adjectives.
  const singleAtomToSymbol: Record<string, string> = {
    'amine': 'N',
    'ether': 'O',
    'sulfide': 'S',
  };

  // PIPH-106: Defensive check for missing internalName
  if (!fg.internalName) {
    // Fallback to group name (stripped of "of {parent}")
    return stripOfSuffix(fg.name);
  }

  const baseType = fg.internalName.split('.')[0];

  // Check if this is a single-atom group that should use element name
  if (singleAtomToSymbol[baseType]) {
    const symbol = singleAtomToSymbol[baseType];
    // PIPH-310: Surface charge/radical on the underlying atom in the prose.
    const atomIdx = fg.atomIndices[0];
    const atom = atomIdx !== undefined
      ? serialized.atoms.find(a => a.index === atomIdx)
      : undefined;
    return elementDisplayName(symbol, {
      charge: atom?.charge,
      radicals: atom?.radicals,
    });
  }

  // Multi-atom groups (ester, amide, lactone, lactam) use group name
  return stripOfSuffix(fg.name);
}

/**
 * PIPH-106: Filter Pattern D groups to suppress single-atom groups (ether, amine, sulfide)
 * whose atoms overlap with multi-atom groups (lactone, lactam). This prevents the ether
 * oxygen in a lactone from appearing separately as "with oxygen" when it should be
 * "with ester".
 */
function filterPatternDGroups(
  patternDGroups: SerializedFeature[]
): SerializedFeature[] {
  const singleAtomTypes = new Set(['amine', 'ether', 'sulfide']);
  const atomsClaimedByMultiAtomPatternD = new Set<number>();

  // First pass: collect atoms claimed by multi-atom Pattern D groups
  for (const fg of patternDGroups) {
    const baseType = fg.internalName?.split('.')[0] ?? '';
    if (!singleAtomTypes.has(baseType)) {
      // This is a multi-atom Pattern D group - claim its atoms
      for (const atomIdx of fg.atomIndices) {
        atomsClaimedByMultiAtomPatternD.add(atomIdx);
      }
    }
  }

  // Second pass: filter out single-atom groups whose atoms overlap with multi-atom groups
  return patternDGroups.filter(fg => {
    const baseType = fg.internalName?.split('.')[0] ?? '';
    if (!singleAtomTypes.has(baseType)) {
      // Multi-atom groups keep themselves
      return true;
    }
    // Single-atom groups: check if their atoms overlap with multi-atom groups
    const hasOverlap = fg.atomIndices.some(idx => atomsClaimedByMultiAtomPatternD.has(idx));
    return !hasOverlap;
  });
}

// NOTE: isIntegratedGroup() was deprecated and replaced by isPatternDGroup()
// which uses classifyRelationship() for proper Pattern D detection per PIPH-60.

/**
 * PIPH-234: Cluster-aware version of the original `getAttachedAtomFeatures`
 * (now removed) for the
 * workspace summary path. Groups connected unrecognized atom features
 * (e.g. an `O-O-H` peroxide chain) and returns one entry per cluster
 * with the cluster's rolled-up display name (`HO₂` rather than two
 * separate `Oxygen-A` / `Oxygen-B` entries).
 *
 * Applies the PIPH-139 bridging exclusion to the cluster as a WHOLE: if
 * any atom in the cluster touches a different primary, the entire
 * cluster is excluded — it'll be described in connection sentences
 * instead of in the "with X" clause.
 */
function getAttachedAtomClusters(
  primaryId: string,
  serialized: SerializationResult,
): { displayName: string; key: string }[] {
  const primary = serialized.features.find(f => f.id === primaryId);
  if (!primary) return [];

  const primaryAtomSet = new Set(primary.atomIndices);
  const allPrimaries = serialized.features.filter(f => PRIMARY_TYPES.has(f.type));
  const allAtomFeatures = serialized.features.filter(f => f.type === 'atom');

  const clusters = groupUnclaimedAtomFeaturesIntoClusters(allAtomFeatures, serialized);

  const result: { displayName: string; key: string }[] = [];
  for (const cluster of clusters) {
    // Cluster must touch this primary at least once.
    const touchesThisPrimary = cluster.connectionAtomIndices.some(idx =>
      primaryAtomSet.has(idx),
    );
    if (!touchesThisPrimary) continue;

    // PIPH-139 (cluster-wide): exclude the cluster from "with X" if any of
    // its connection atoms lands in a DIFFERENT primary.
    const touchedPrimaries = allPrimaries.filter(p => {
      const pAtomSet = new Set(p.atomIndices);
      return cluster.connectionAtomIndices.some(idx => pAtomSet.has(idx));
    });
    if (touchedPrimaries.length !== 1) continue;

    // PIPH-310: For singleton atom-feature clusters (lone heteroatom attached
    // to a primary), surface charge/radical adjectives in the "with X" clause
    // by routing through `elementDisplayName` instead of using the feature
    // name (which omits radical state and only encodes charge as a
    // superscript on the formula).
    let displayName: string;
    if (cluster.features.length > 1) {
      displayName = renderClusterDisplayNameForSummary(cluster, serialized);
    } else {
      const lone = cluster.features[0];
      const loneAtomIdx = lone.atomIndices.length === 1
        ? lone.atomIndices[0]
        : undefined;
      const atom = loneAtomIdx !== undefined
        ? serialized.atoms.find(a => a.index === loneAtomIdx)
        : undefined;
      if (atom && (atom.radicals ?? 0) > 0) {
        displayName = elementDisplayName(atom.symbol, {
          charge: atom.charge,
          radicals: atom.radicals,
        });
      } else {
        displayName = lone.name;
      }
    }
    const key = cluster.features.map(f => f.id).sort().join('|');
    result.push({ displayName, key });
  }
  return result;
}

/**
 * PIPH-234: Compose the rolled-up display name for a multi-atom cluster
 * from this module. Mirrors the behavior of `renderClusterDisplayName`
 * in `buildStructuredAltText.ts`, just without going through the
 * `UnclaimedAtom` interface (which is internal to the per-feature block
 * path). Kept local to avoid circular imports.
 */
function renderClusterDisplayNameForSummary(
  cluster: ReturnType<typeof groupUnclaimedAtomFeaturesIntoClusters>[number],
  serialized: SerializationResult,
): string {
  // Hydrogens are folded into each heavy atom's `hydrogenCount` during
  // serialization; expand them back out here so the formula reflects
  // the cluster's full atom set (heavy + bonded H).
  const symbols: string[] = [];
  const seen = new Set<number>();
  for (const f of cluster.features) {
    for (const idx of f.atomIndices) {
      if (seen.has(idx)) continue;
      seen.add(idx);
      const atom = serialized.atoms.find(a => a.index === idx);
      if (!atom) continue;
      if (atom.symbol === 'H') {
        symbols.push('H');
        continue;
      }
      symbols.push(atom.symbol);
      for (let i = 0; i < (atom.hydrogenCount ?? 0); i++) symbols.push('H');
    }
  }
  const formula = assembleMolecularFormula(symbols);
  const chargeSuffix = formatChargeSuffix(cluster.netCharge);
  const radicalSuffix = cluster.hasRadical ? ' radical' : '';
  return formula + chargeSuffix + radicalSuffix;
}

/**
 * Build a moderate tier summary.
 * Pattern: "A {core with groups}[, fused to {fused}][, and a {connected}].
 *          Additional groups include {non-core groups}."
 *
 * PIPH-106: Pattern D groups (integrated into ring) are folded into the base
 * sentence using element names (for single-atom groups) or group names
 * (for multi-atom groups like ester/lactone).
 *
 * PIPH-127: When a molecule has BOTH fusions AND other attachments (groups or
 * non-fused connections), use multi-sentence pattern to avoid ambiguity:
 * - Sentence 1: Core + Pattern D groups, fused to fused feature(s). Nothing else.
 * - Sentence 2: Core's remaining groups and non-fused connections with their groups.
 *
 * Per-feature labels: source-side connection atoms resolve in the source
 * feature's per-feature scope.
 */
function buildModerateSummary(
  core: SerializedFeature,
  primaries: SerializedFeature[],
  functionalGroups: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  const sentences: string[] = [];

  // PIPH-212: Functional groups that span two primaries are described in the
  // connection sentence and should NOT also appear as substituents.
  const isInterPrimaryGroup = (fg: SerializedFeature): boolean =>
    classifyRelationship(fg, serialized).kind === 'between';

  // Get functional groups on core and separate Pattern D from others
  const coreGroups = functionalGroups.filter(fg => fg.parentFeatureId === core.id);
  const corePatternD = filterPatternDGroups(coreGroups.filter(fg => isPatternDGroup(fg, serialized)));
  const coreOther = coreGroups.filter(
    fg => !isPatternDGroup(fg, serialized) && !isInterPrimaryGroup(fg),
  );

  // Get connections from core
  const coreConnections = connectionMap.get(core.id) || [];
  const fusedConnections = coreConnections.filter(c => c.isFused);
  const nonFusedConnections = coreConnections.filter(c => !c.isFused);

  // PIPH-127: Detect if we need multi-sentence pattern for fusion + attachments
  const hasFusion = fusedConnections.length > 0;
  const hasNonFusedOrGroups = nonFusedConnections.length > 0 || coreOther.length > 0;
  const needsMultiSentence = hasFusion && hasNonFusedOrGroups;

  if (needsMultiSentence) {
    // PIPH-127: Multi-sentence pattern for fusion + attachments

    // First sentence: Core with Pattern D groups + fusions only
    const firstSentenceParts: string[] = [];

    let corePart = `A ${core.name}`;
    if (corePatternD.length > 0) {
      const patternDNames = corePatternD.map(fg => getPatternDName(fg, serialized));
      corePart += ` with ${joinList(patternDNames)}`;
    }
    firstSentenceParts.push(corePart);

    // Add fused relationships (without their non-Pattern-D groups - those go in "Additional groups")
    const fusedDescriptions = fusedConnections.map(conn => {
      const fusedPrimary = primaries.find(p => p.id === conn.targetFeatureId);
      if (!fusedPrimary) return '';
      return `a ${fusedPrimary.name}`;
    }).filter(Boolean);

    if (fusedDescriptions.length > 0) {
      firstSentenceParts.push(`fused to ${joinList(fusedDescriptions)}`);
    }

    sentences.push(firstSentenceParts.join(', ') + '.');

    // Second sentence: Core with other groups + non-fused connections with their groups
    const secondParts: string[] = [];

    let coreSecond = core.name;
    const coreSecondWithParts: string[] = [];
    if (coreOther.length > 0) {
      coreSecondWithParts.push(...groupDuplicateFunctionalGroups(coreOther, 'moderate'));
    }
    // PIPH-131 / PIPH-234: include cluster-rolled-up unrecognized atoms
    const coreAtomClusters = getAttachedAtomClusters(core.id, serialized);
    if (coreAtomClusters.length > 0) {
      coreSecondWithParts.push(...coreAtomClusters.map(c => c.displayName));
    }
    if (coreSecondWithParts.length > 0) {
      coreSecond += ` with ${joinList(coreSecondWithParts)}`;
    }
    secondParts.push(coreSecond);

    // Add non-fused connections with their functional groups
    // PIPH-128: Use "connected at position to" language
    for (const conn of nonFusedConnections) {
      const connected = primaries.find(p => p.id === conn.targetFeatureId);
      if (!connected) continue;

      // Get functional groups on the connected primary (excluding Pattern D
      // and PIPH-212 inter-primary groups already described in the connection)
      const connGroups = functionalGroups.filter(fg => fg.parentFeatureId === connected.id);
      const connOther = connGroups.filter(
        fg => !isPatternDGroup(fg, serialized) && !isInterPrimaryGroup(fg),
      );

      // PIPH-128: Add position label
      // PIPH-139: Include bridging group info when present
      // PIPH-212: Include "by double bond" for alkene/alkyne direct connections.
      // Source atom is in `core`'s scope so the resolver receives core.id.
      const posLabel = getConnectionPositionLabel(core, conn.sourceAtomIndices, resolve);
      const connectorPhrase = formatConnectorPhrase(conn, serialized);
      let connPart = posLabel
        ? `connected at ${posLabel}${connectorPhrase} to ${connected.name}`
        : `connected${connectorPhrase} to ${connected.name}`;

      const connWithParts: string[] = [];
      if (connOther.length > 0) {
        connWithParts.push(...groupDuplicateFunctionalGroups(connOther, 'moderate'));
      }
      // PIPH-131 / PIPH-234: include cluster-rolled-up unrecognized atoms
      const connAtomClusters = getAttachedAtomClusters(connected.id, serialized);
      if (connAtomClusters.length > 0) {
        connWithParts.push(...connAtomClusters.map(c => c.displayName));
      }
      if (connWithParts.length > 0) {
        connPart += ` with ${joinList(connWithParts)}`;
      }
      secondParts.push(connPart);
    }

    sentences.push(secondParts.join(', ') + '.');

  } else {
    // Original single-sentence flow (no fusion + attachments combo)

    // Build first sentence parts
    const firstSentenceParts: string[] = [];

    // Core with its direct groups - Pattern D groups folded in
    let corePart = `A ${core.name}`;
    const coreWithParts: string[] = [];

    if (corePatternD.length > 0) {
      coreWithParts.push(...corePatternD.map(fg => getPatternDName(fg, serialized)));
    }
    if (coreOther.length > 0) {
      coreWithParts.push(...groupDuplicateFunctionalGroups(coreOther, 'moderate'));
    }

    // PIPH-131 / PIPH-234: include cluster-rolled-up unrecognized atoms
    const coreAtomClustersForFirst = getAttachedAtomClusters(core.id, serialized);
    if (coreAtomClustersForFirst.length > 0) {
      coreWithParts.push(...coreAtomClustersForFirst.map(c => c.displayName));
    }

    if (coreWithParts.length > 0) {
      corePart += ` with ${joinList(coreWithParts)}`;
    }
    firstSentenceParts.push(corePart);

    // Add fused relationships with Pattern D groups folded in
    if (fusedConnections.length > 0) {
      const fusedDescriptions = fusedConnections.map(conn => {
        const fusedPrimary = primaries.find(p => p.id === conn.targetFeatureId);
        if (!fusedPrimary) return '';

        // Get Pattern D groups for this fused ring
        const fusedGroups = functionalGroups.filter(fg => fg.parentFeatureId === fusedPrimary.id);
        const fusedPatternD = filterPatternDGroups(fusedGroups.filter(fg => isPatternDGroup(fg, serialized)));

        if (fusedPatternD.length > 0) {
          const patternDNames = fusedPatternD.map(fg => getPatternDName(fg, serialized));
          return `a ${fusedPrimary.name} with ${joinList(patternDNames)}`;
        }
        return `a ${fusedPrimary.name}`;
      }).filter(Boolean);

      if (fusedDescriptions.length > 0) {
        firstSentenceParts.push(`fused to ${joinList(fusedDescriptions)}`);
      }
    }

    // Add non-fused connected primaries with position labels
    // PIPH-128: Use "connected at position to" for non-fused connections
    // PIPH-139: Include bridging group info when present
    // PIPH-212: Include "by double bond" for alkene/alkyne direct connections
    for (const conn of nonFusedConnections) {
      const connected = primaries.find(p => p.id === conn.targetFeatureId);
      if (!connected) continue;

      const posLabel = getConnectionPositionLabel(core, conn.sourceAtomIndices, resolve);
      const connectorPhrase = formatConnectorPhrase(conn, serialized);
      if (posLabel) {
        firstSentenceParts.push(`connected at ${posLabel}${connectorPhrase} to ${connected.name}`);
      } else {
        firstSentenceParts.push(`connected${connectorPhrase} to ${connected.name}`);
      }
    }

    sentences.push(firstSentenceParts.join(', ') + '.');

    // Build second sentence: Additional groups on non-core primaries
    // Exclude Pattern D groups (they're already in the first sentence)
    const nonCorePrimaries = primaries.filter(p => p.id !== core.id);
    const additionalGroups: SerializedFeature[] = [];

    for (const primary of nonCorePrimaries) {
      const groups = functionalGroups.filter(fg => {
        if (fg.parentFeatureId !== primary.id) return false;
        // Exclude Pattern D groups (they're in first sentence for fused, or should be folded)
        if (isPatternDGroup(fg, serialized)) return false;
        // PIPH-212: Exclude inter-primary groups (described in connection sentence).
        if (isInterPrimaryGroup(fg)) return false;
        return true;
      });

      additionalGroups.push(...groups);
    }

    if (additionalGroups.length > 0) {
      const grouped = groupDuplicateFunctionalGroups(additionalGroups, 'moderate');
      sentences.push(`Additional groups include ${joinList(grouped)}.`);
    }
  }

  return sentences.join(' ');
}

/**
 * Build a complex tier summary.
 * Pattern: "Contains {primary features listed}, {functional groups listed}."
 */
function buildComplexSummary(
  primaries: SerializedFeature[],
  functionalGroups: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>,
  serialized: SerializationResult
): string {
  // Group duplicate primaries
  const groupedPrimaries = groupDuplicatePrimaries(primaries);

  // Group duplicate functional groups (pluralize)
  const groupedFunctionalGroups = groupDuplicateFunctionalGroups(functionalGroups, 'complex');

  // Build fused relationships string
  // Find all fused pairs and describe them
  const fusedPairs = new Set<string>();
  const fusedDescriptions: string[] = [];

  for (const primary of primaries) {
    const connections = connectionMap.get(primary.id) || [];
    for (const conn of connections) {
      if (conn.isFused) {
        const pairKey = [primary.id, conn.targetFeatureId].sort().join('|');
        if (!fusedPairs.has(pairKey)) {
          fusedPairs.add(pairKey);
          const target = primaries.find(p => p.id === conn.targetFeatureId);
          if (target) {
            fusedDescriptions.push(`${primary.name} fused to ${target.name}`);
          }
        }
      }
    }
  }

  // PIPH-131 / PIPH-234: Get cluster-rolled-up unrecognized atom entries
  // attached to any primary. Dedup by cluster key (sorted feature IDs)
  // so a cluster shared across primaries appears once.
  const seenClusterKeys = new Set<string>();
  const atomFeatureNames: string[] = [];
  for (const primary of primaries) {
    const attached = getAttachedAtomClusters(primary.id, serialized);
    for (const c of attached) {
      if (seenClusterKeys.has(c.key)) continue;
      seenClusterKeys.add(c.key);
      atomFeatureNames.push(c.displayName);
    }
  }

  // Combine all features
  const allFeatures: string[] = [];

  // Add fused descriptions first if any
  if (fusedDescriptions.length > 0) {
    // Replace the grouped primaries that are part of fused systems
    // For simplicity, just list all primaries and functional groups
    allFeatures.push(...groupedPrimaries);
  } else {
    allFeatures.push(...groupedPrimaries);
  }

  allFeatures.push(...groupedFunctionalGroups);

  // PIPH-131: Include atom-type features
  allFeatures.push(...atomFeatureNames);

  return `Contains ${joinList(allFeatures)}.`;
}

// ============================================================================
// Single Molecule Summary
// ============================================================================

/**
 * Generate a summary for a single molecule.
 *
 * PIPH-113: Exported for use in buildAltTextMap where per-molecule descriptions
 * are needed (instead of the workspace-level buildWorkspaceSummary).
 *
 * PIPH-109: For simple molecules with no recognized features, use molecular formula
 * (e.g., "AlCl₃", "H₂O") instead of generic descriptions.
 *
 * Per-feature naming: caller supplies an `AtomLabelResolver` bound to
 * `serialized` (typically built once per molecule).
 *
 * @param serialized - Serialized feature result for this molecule
 * @param _index - Molecule index (unused, for backward compatibility)
 * @param _totalMolecules - Total molecule count (unused, for backward compatibility)
 * @param allAtomSymbols - Optional: all atom symbols in this molecule (used when
 *                         features don't cover all atoms, e.g., simple inorganic molecules)
 * @param netCharge - Optional: net formal charge of the molecule. When non-zero,
 *                    appended as a Unicode superscript suffix to the formula
 *                    (e.g., H₃O → H₃O⁺ for charge +1).
 * @param resolve - AtomLabelResolver bound to `serialized`.
 */
export function describeSingleMolecule(
  serialized: SerializationResult,
  _index: number,
  _totalMolecules: number,
  allAtomSymbols: string[] | undefined,
  netCharge: number | undefined,
  resolve: AtomLabelResolver,
): string {
  // Get primary features
  const primaries = serialized.features.filter(f => isPrimaryType(f.type));

  // Handle simple molecules (no primary features - like water, HCl, AlCl₃)
  if (primaries.length === 0) {
    // PIPH-233: append " radical" to the molecule label when any atom in this
    // molecule carries a radical (e.g. "Br" → "Br radical").
    const radicalSuffix = serialized.atoms.some(a => (a.radicals ?? 0) > 0) ? ' radical' : '';

    // PIPH-109: Use molecular formula for simple molecules
    // First try allAtomSymbols (complete list), then fallback to serialized.atoms
    const atomSymbols = allAtomSymbols ?? serialized.atoms.map(a => a.symbol);
    if (atomSymbols.length > 0) {
      const formula = assembleMolecularFormula(atomSymbols);
      if (formula) {
        return formula + formatChargeSuffix(netCharge) + radicalSuffix;
      }
    }

    // Check for molecule-type features (simple molecules with names)
    const moleculeFeature = serialized.features.find(f => f.type === 'molecule');
    if (moleculeFeature) {
      return moleculeFeature.name + radicalSuffix;
    }

    // Check for just atoms
    const atomFeatures = serialized.features.filter(f => f.type === 'atom');
    if (atomFeatures.length > 0) {
      return atomFeatures.map(f => f.name).join(' ') + radicalSuffix;
    }

    // Just list functional groups
    const fgFeatures = serialized.features.filter(f => f.type === 'functional_group');
    if (fgFeatures.length > 0) {
      return fgFeatures.map(f => f.name).join(', ') + radicalSuffix;
    }

    return 'empty structure';
  }

  // Get functional groups
  const functionalGroups = serialized.features.filter(f => f.type === 'functional_group');

  // Compute connections
  const connectionMap = computePrimaryConnections(primaries, serialized);

  // Determine core
  const core = determineCore(primaries, connectionMap);
  if (!core) {
    return 'unknown structure';
  }

  // Get core connection count
  const coreConnectionCount = connectionMap.get(core.id)?.length || 0;

  // Classify complexity
  const tier = classifyComplexity(primaries, functionalGroups, coreConnectionCount);

  // Build summary based on tier
  switch (tier) {
    case 'simple':
      return buildSimpleSummary(core, primaries, functionalGroups, connectionMap, serialized, resolve);
    case 'moderate':
      return buildModerateSummary(core, primaries, functionalGroups, connectionMap, serialized, resolve);
    case 'complex':
      return buildComplexSummary(primaries, functionalGroups, connectionMap, serialized);
  }
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Build a workspace summary template for accessibility.
 *
 * Generates a top-level orientation description that tells users what they're
 * looking at before any detail. Used as workspace-level text in SVG navigation
 * and LLM alt text prompts.
 *
 * @example Single simple molecule
 * "A 6-membered ring with alcohol and primary amine."
 *
 * @example Single moderate molecule
 * "A benzene ring with alcohol, fused to a 5-membered ring with nitrogen and alkene,
 *  and a 2-carbon chain. Additional groups include primary amine."
 *
 * @example Multiple molecules
 * "Two molecules. Molecule 1: a 6-membered ring with alcohol. Molecule 2: water."
 */
export function buildWorkspaceSummary(
  workspace: MolecularWorkspace,
  featureResult: WorkspaceFeatureResult,
  sortedComponents?: Array<{ atomIds: string[]; bondIds: string[] }>,
): string {
  // PIPH-320: Workspace-level summary is orientation-only — what's here
  // (molecular formulas) and what's moving (simplified arrow descriptions).
  // Structural description belongs at the molecule level; consumers that
  // want that drill into `describeSingleMolecule` directly.
  const components = sortedComponents ?? findConnectedComponents(workspace);
  if (components.length === 0) return 'Empty workspace.';

  // "{count} molecule(s): {formula list}."
  const formulas = components.map(c => moleculeFormula(c, workspace, featureResult));
  const moleculeLine =
    components.length === 1
      ? `1 molecule: ${formulas[0]}.`
      : `${components.length} molecules: ${joinAnd(formulas)}.`;

  const arrows = workspace.getArrows();
  if (arrows.length === 0) return moleculeLine;

  // Arrows live at the workspace level — they can cross molecules — so we
  // serialize the whole feature set once to get a single atomIndex map for
  // looking up charges / radicals. This mirrors what `buildCurvedArrowBlock`
  // already does for the navigation-level arrow block.
  const allFeatures = featureResult.features.map(wf => wf.feature);
  const serialized = serializeFeatures(allFeatures, featureResult.graph);
  const atomIdToIndex = new Map<string, number>();
  for (let i = 0; i < featureResult.indexToAtomId.length; i++) {
    atomIdToIndex.set(featureResult.indexToAtomId[i], i);
  }

  // Shared brevity state across all arrows in this workspace summary.
  const mentioned = new Set<number>();
  const arrowSentences = arrows.map(arrow =>
    describeArrowOrientation(arrow, workspace, serialized, atomIdToIndex, mentioned),
  );

  return `${moleculeLine} ${arrowSentences.join('. ')}.`;
}
